
import { AuthentificationService } from './authentification.service';
import { ResolvedPermission, PermissionNode, PermissionType, EntityActionPermission, FeatureActionPermission, PermissionState } from '../models/permissions/permission.model';
import { Injectable } from '@angular/core';
import { Account as ManagerAccount } from '../models/matwins-manager/Account.model';
import { Account as TenantAccount } from '../models/matwins-tenant/Account.model';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  private resolvedPermissions: ResolvedPermission[] = [];

  private currentTree: PermissionNode | null = null;


  private _currentUser!: ManagerAccount | TenantAccount | null;


  isUserAuthenticated: boolean = false;

  public get currentUser(): ManagerAccount | TenantAccount | null {
    return this._currentUser;
  }

  public set currentUser(user: ManagerAccount | TenantAccount | null) {

    this._currentUser = user;
    // enregistrer les permissions courantes
    this.LoadPermissionsFromJson(this._currentUser?.permissions ?? '{}');
  }

  constructor() { } //private authorisationService: AuthentificationService

  //  Resolution de l'arbre en liste plate
  public ResolvePermissions(root: PermissionNode): ResolvedPermission[] {
    this.resolvedPermissions = this.flattenPermissions(root);
    return this.resolvedPermissions;
  }

  private flattenPermissions(node: PermissionNode, parentPath: string[] = []): ResolvedPermission[] {
    const results: ResolvedPermission[] = [];
    const currentPath = [...parentPath, node.value];

    const type = this.detectPermissionType(node);

    if (type === PermissionType.Entity || type === PermissionType.Feature) {
      const actions = type === PermissionType.Entity
        ? this.extractEntityActions(node.children!)
        : this.extractFeatureActions(node.children!);

      results.push({
        name: node.value,
        fullPath: currentPath.join('.'),
        type,
        state: node.state,
        actions
      });
    }

    if (node.children) {
      node.children.forEach(child => {
        results.push(...this.flattenPermissions(child, currentPath));
      });
    }

    return results;
  }

  private detectPermissionType(node: PermissionNode): PermissionType {
    if (['Permissions', 'Entities', 'Features'].includes(node.key)) {
      return PermissionType.Group;
    }
    if (node.children?.some(c => ['Create', 'Read', 'Update', 'Delete'].includes(c.key))) {
      return PermissionType.Entity;
    }
    if (node.children?.some(c => c.key === 'Show')) {
      return PermissionType.Feature;
    }
    return PermissionType.Group;
  }

  private extractEntityActions(children: PermissionNode[]): EntityActionPermission {
    const map = new Map<string, PermissionNode>();
    children.forEach(c => map.set(c.key, c));

    return {
      Create: map.get('Create')?.state === 'selected',
      Read: map.get('Read')?.state === 'selected',
      Update: map.get('Update')?.state === 'selected',
      Delete: map.get('Delete')?.state === 'selected'
    };
  }

  private extractFeatureActions(children: PermissionNode[]): FeatureActionPermission {
    const show = children.find(c => c.key === 'Show');
    return { Show: show?.state === 'selected' };
  }

  //  Verification d'une permission par fullPath + action
  public CheckPermission(fullPathWithAction: string): boolean {
    const parts = fullPathWithAction.split('.');
    const action = parts.pop();
    const fullPath = parts.join('.');

    if (!action || !fullPath) return false;

    const perm = this.resolvedPermissions.find(p => p.fullPath === fullPath);
    if (!perm) return false;

    if (perm.type === PermissionType.Entity) {
      const actions = perm.actions as EntityActionPermission;
      return !!(actions as any)[action];
    }

    if (perm.type === PermissionType.Feature) {
      return action === 'Show' && (perm.actions as FeatureActionPermission).Show;
    }

    return false;
  }

  //  Generation des permissions par defaut pour un role
  public GenerateDefaultPermissionsForRole(entityNames: string[], featureNames: string[]): PermissionNode {

    const entityChildren: PermissionNode[] = entityNames.map(name => ({
      key: name,
      value: name,
      state: 'selected' as PermissionState, // full-select pour toutes les actions
      children: ['Create', 'Read', 'Update', 'Delete'].map(action => ({
        key: action,
        value: action,
        state: 'selected' as PermissionState
      }))
    }));

    const featureChildren: PermissionNode[] = featureNames.map(name => ({
      key: name,
      value: name,
      state: 'selected' as PermissionState,
      children: [{
        key: 'Show',
        value: 'Show',
        state: 'selected' as PermissionState
      }]
    }));

    const hasEntities = entityChildren.length > 0;
    const hasFeatures = featureChildren.length > 0;

    return {
      key: 'Permissions',
      value: 'Permissions',
      expanded: true,
      state: (hasEntities || hasFeatures) ? 'selected' as PermissionState : 'not-selected' as PermissionState,
      children: [
        {
          key: 'Entities',
          value: 'Entities',
          state: hasEntities ? 'selected' as PermissionState : 'not-selected' as PermissionState,
          children: entityChildren
        },
        {
          key: 'Features',
          value: 'Features',
          state: hasFeatures ? 'selected' as PermissionState : 'not-selected' as PermissionState,
          children: featureChildren
        }
      ]
    };
  }

  //  Nettoyage : supprimer tout ce qui est 'not-selected' pour calculer les permissions max d'un compte
  public InferAccountPermissionsFromDefault(tree: PermissionNode): PermissionNode | null {
    return this.cleanTree(tree);
  }

  private cleanTree(node: PermissionNode): PermissionNode | null {
    // Cas feuille
    if (!node.children || node.children.length === 0) {
      return node.state !== 'not-selected' ? { ...node } : null;
    }

    // Cas noeuds avec enfants
    const survivingChildren = node.children
      .map(child => this.cleanTree(child))     // appel recursif
      .filter((child): child is PermissionNode => child !== null);

    if (survivingChildren.length === 0) {
      return node.state === 'selected' ? { ...node, children: [] } : null;
    }

    // Recalcul du state du parent
    const allSelected = survivingChildren.every(c => c.state === 'selected');
    const newState: PermissionState = allSelected ? 'selected' : 'partial-select';

    return {
      ...node,
      state: newState,
      children: survivingChildren
    };
  }


  /**
 * Parse une chaîne JSON contenant l'arbre des permissions
 * @param jsonString Le string JSON des permissions provenant de la BD 
 * @returns PermissionNode | null (null si erreur de parsing ou données invalides)
 */
  public ParsePermissionsFromJson(jsonString: string): PermissionNode | null {
    if (!jsonString || typeof jsonString !== 'string') {
      console.warn('parsePermissionsFromJson: jsonString vide');
      return null;
    }

    try {
      const parsed = JSON.parse(jsonString);

      // Validation basique de la structure attendue
      if (!parsed || typeof parsed !== 'object') {
        console.warn('parsePermissionsFromJson: JSON parsé n\'est pas un objet');
        return null;
      }

      if (!('key' in parsed) || !('value' in parsed) || !('state' in parsed)) {
        console.warn('parsePermissionsFromJson: Structure minimale manquante (key, value, state)');
        return null;
      }

      // Validation recursive du state et des enfants
      return this.validateAndCastNode(parsed) ? parsed as PermissionNode : null;

    } catch (error) {
      console.error('parsePermissionsFromJson: Erreur de parsing JSON', error);
      return null;
    }
  }

  private validateAndCastNode(node: any): node is PermissionNode {
    if (!node || typeof node !== 'object') return false;

    if (typeof node.key !== 'string' || typeof node.value !== 'string') return false;

    const validStates: PermissionState[] = ['selected', 'partial-select', 'not-selected'];
    if (!validStates.includes(node.state)) return false;

    if (node.children !== undefined) {
      if (!Array.isArray(node.children)) return false;
      for (const child of node.children) {
        if (!this.validateAndCastNode(child)) return false;
      }
    }

    // expanded est optionnel, on l'accepte s'il est boolean
    if (node.expanded !== undefined && typeof node.expanded !== 'boolean') return false;

    return true;
  }

  // =====================   ===================  ===============


  // definit les permissions actuelles 
 public SetCurrentPermissions(tree: PermissionNode | null) {
    this.currentTree = tree;
    this.resolvedPermissions = tree ? this.flattenPermissions(tree) : [];
  }


  public LoadPermissionsFromJson(jsonString: string): boolean {
    const tree = this.ParsePermissionsFromJson(jsonString);
    if (tree) {
      this.SetCurrentPermissions(tree);
      return true;
    }
    return false;
  }


  public UserHasAllPermissions(required: string[]): boolean {
    if (!this.isUserAuthenticated || !this.currentUser) {
      return false;
    }

    if (required.length === 0) {
      return true; // aucune permission requise ==> toujours autorise
    }

    let result = required.every(perm => this.UserHasSinglePermission(perm));

    return result; 
  }


  public UserHasSinglePermission(fullPathWithAction: string): boolean {
    if (!this.isUserAuthenticated || !this.currentUser) {
      return false;
    }

    const parts = fullPathWithAction.split('.');
    if (parts.length < 3) return false; // format invalide

    const action = parts.pop()!; // derniere partie = action
    const fullPath = parts.join('.');

    const perm = this.resolvedPermissions.find(p => p.fullPath === fullPath); 
    if (!perm) return false;

    if (perm.type === PermissionType.Entity) {
      const actions = perm.actions as EntityActionPermission;
      switch (action) {
        case 'Create': return actions.Create;
        case 'Read': return actions.Read;
        case 'Update': return actions.Update;
        case 'Delete': return actions.Delete;
        default: return false;
      }
    }

    if (perm.type === PermissionType.Feature) {
      return action === 'Show' && (perm.actions as FeatureActionPermission).Show;
    }

    return false;
  }


  public GetResolvedPermissions(): ResolvedPermission[] {
    return [...this.resolvedPermissions];
  }




}
